import React, { useEffect, useState } from "react";
import {
  FormAutocomplete,
  LabeledAutocomplete,
  formClasses,
  labelClasses,
} from "../styles/AutocompleteStyles";
import { TextField } from "@mui/material";
import { useLazyQuery } from "@apollo/client";

import useUrlParams from "../customHooks/useUrlParams";
import { FETCH_MORE_DATA, ROWS_PER_PAGE, debouncedDelay } from "../utils/constants";
import { OrderDirection } from "../utils/commonQueryTypes";
import { getModifiedScrollHeight } from "../utils/UtilFunctions";
import { AtsSubsidiaryOrderField } from "../components/modules/asset/masters/subsidary/queries/types";
import useLoggedInUserDetails from "../customHooks/useLoggedInUserDetails";
import { GetJunctions } from "../components/modules/asset/masters/subsidary/junctions/queries/queries";
import {
  GetJunctionsData,
  GetJunctionsVars,
  JunctionEdge,
  JunctionQueryType,
} from "../components/modules/asset/masters/subsidary/junctions/queries/types";
import { useDebounce } from "../customHooks/useDebounce";

interface Props {
  onKeyDown:
    | ((
        event: React.KeyboardEvent<HTMLDivElement> & {
          defaultMuiPrevented?: boolean;
        }
      ) => void)
    | undefined;
  onChange: (newValue: JunctionEdge) => void;
  value: number;
  label: string;
  autoFocus?: boolean;
  companyID?: number;
  subsidiaryID?: number;
  siteID?: number;
  placeholder?: string;
}
const JunctionsAutocomplete = ({
  onChange,
  onKeyDown,
  autoFocus,
  value,
  label,
  companyID,
  subsidiaryID,
  siteID,
  placeholder,
}: Props) => {
  const { customer_id, company_id } = useUrlParams();
  const [searchData, setSearchData] = useState("");
  const debouncedSearch = useDebounce(searchData, debouncedDelay);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [junctions, setJunctions] = useState<JunctionEdge[]>([]);
  const [autoValue, setAutoValue] = useState<JunctionEdge | null>(null);

  const [GetJunction, { data, loading, fetchMore }] = useLazyQuery<
    GetJunctionsData,
    GetJunctionsVars
  >(GetJunctions);

  const { user_details } = useLoggedInUserDetails();
  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetJunctions.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = junctions.find(
            (employee) => employee.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setJunctions(updatedNewData);
      } else {
        setJunctions(newData);
      }
      setEndCursor(data.GetJunctions.pageInfo.endCursor);
    }
    // eslint-disable-next-line
  }, [data, loading]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (customer_id) {
          GetJunction({
            variables: {
              after: null,
              customerID: customer_id,
              companyID: companyID ? companyID : 0,
              subsidiaryID: subsidiaryID ? subsidiaryID : 0,
              siteID: siteID ? siteID : 0,
              empID: user_details.id,
              first: ROWS_PER_PAGE,
              orderBy: {
                direction: OrderDirection.ASC,
                field: AtsSubsidiaryOrderField.NAME,
              },
              ldgrID: 0,

              name: debouncedSearch,
              queryType: JunctionQueryType.ALL
            },
          });
        }
      } catch (err) {
        console.error((err as Error).message);
      }
    };
    fetchData();
  }, [
    customer_id,
    company_id,
    GetJunction,
    user_details.id,
    debouncedSearch,
    companyID,
    subsidiaryID,
    siteID,
  ]);

  useEffect(() => {
    if (value) {
      const foundValue = junctions.find(
        ({ node }) => Number(node.id) === Number(value)
      );

      setAutoValue(foundValue ? foundValue : null);
    } else {
      setAutoValue(null);
    }
  }, [value, data, junctions]);
  const handleScroll = (e: React.UIEvent<HTMLUListElement, UIEvent>) => {
    const target = e.target as HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
      if (hasNextPage && !loading) {
        fetchMore({
          variables: {
            first: FETCH_MORE_DATA,
            after: endCursor,
          },
          updateQuery: (prevResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prevResult;

            const newEdges = fetchMoreResult.GetJunctions.edges;
            const pageInfo = fetchMoreResult.GetJunctions.pageInfo;
            setEndCursor(pageInfo.endCursor);
            setHasNextPage(pageInfo.hasNextPage);

            const duplicateCheck = prevResult.GetJunctions.edges.filter(
              ({ node: { id } }) =>
                newEdges.findIndex(
                  ({ node: { id: newId } }) => newId === id
                ) !== -1
            );

            if (duplicateCheck.length > 0) return prevResult;

            return {
              GetJunctions: {
                edges: [...junctions, ...newEdges],
                pageInfo,
                totalCount: data ? data.GetJunctions.totalCount! : 0,
              },
            };
          },
        });
      }
    }
  };

  let commonAutocompleteClassName = label
    ? labelClasses.inputRoot
    : formClasses.inputRoot;
  let commonTextFieldClassName = label
    ? labelClasses.formControlRoot
    : formClasses.formControlRoot;

  const Autocomplete =
    label.length > 0 ? LabeledAutocomplete : FormAutocomplete;

  return (
    <Autocomplete
      className={commonAutocompleteClassName}
      options={junctions}
      onKeyDown={onKeyDown}
      value={autoValue}
      openOnFocus
      forcePopupIcon
      getOptionLabel={(option) =>
        `${(option as JunctionEdge).node.code} (${
          (option as JunctionEdge).node.name
        })`
      }
      slotProps={{
        listbox: {
          onScroll: handleScroll,
        },
      }}
      onChange={(e, newValue) => {
        onChange(newValue as JunctionEdge);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          autoFocus={autoFocus}
          onChange={(e) => setSearchData(e.target.value)}
          className={commonTextFieldClassName}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          placeholder={placeholder}
        />
      )}
    />
  );
};

export default JunctionsAutocomplete;
